import { useRef, useState, type ReactNode } from "react";
import { CircleUserRound, LogOut, Menu, Moon, Search, Sparkles, Sun } from "lucide-react";
import { useHotkeys } from "../hotkeys";
import { HeaderDropdown } from "./HeaderDropdown";
import type { RenderLink } from "./menu";

type Company = { id: string; legalName: string; tradeName: string | null };
export type HeaderProps = {
  userName: string;
  companies: Company[];
  activeCompanyId: string;
  onCompanyChange(id: string): void;
  onLogout(): void;
  /** Sem onSearch o header não mostra a busca global. */
  onSearch?(q: string): void;
  onToggleSidebar(): void;
  sidebarExpanded: boolean;
  sidebarId: string;
  renderLink: RenderLink;
  jobsSlot?: ReactNode;
  notificationsSlot?: ReactNode;
  theme: "light" | "dark";
  onThemeChange(t: "light" | "dark"): void;
  /** Painel do Copiloto (T1.14); ausente = usuário sem copilot:read (botão desabilitado). */
  copilot?: { open: boolean; onToggle(): void; panelId: string };
};

export function Header(p: HeaderProps) {
  const [q, setQ] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  const onSearch = p.onSearch;
  useHotkeys({ "/": () => searchRef.current?.focus() }, { enabled: !!onSearch });
  const company = p.companies.find((c) => c.id === p.activeCompanyId) ?? p.companies[0];
  return (
    <header className="main-header navbar navbar-expand bg-body border-bottom px-3 gap-2">
      <button
        type="button"
        className="btn btn-link text-reset"
        onClick={p.onToggleSidebar}
        aria-label="Abrir ou fechar menu"
        aria-expanded={p.sidebarExpanded}
        aria-controls={p.sidebarId}
      >
        <Menu size={20} aria-hidden="true" />
      </button>
      {p.renderLink("/", "CX ERP", false, "navbar-brand fw-bold d-none d-sm-inline")}
      {onSearch ? (
        <form
          role="search"
          className="flex-grow-1 d-none d-md-block"
          onSubmit={(e) => {
            e.preventDefault();
            if (q.trim()) onSearch(q.trim());
          }}
        >
          <div className="input-group" style={{ maxWidth: 480 }}>
            <span className="input-group-text">
              <Search size={16} aria-hidden="true" />
            </span>
            <input
              ref={searchRef}
              type="search"
              className="form-control"
              placeholder="Buscar contatos (/)"
              aria-label="Busca global"
              aria-keyshortcuts="/"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
        </form>
      ) : (
        <div className="flex-grow-1" />
      )}
      {p.companies.length > 1 ? (
        <select
          className="form-select w-auto header-company"
          aria-label="Empresa"
          value={p.activeCompanyId}
          onChange={(e) => p.onCompanyChange(e.target.value)}
        >
          {p.companies.map((c) => (
            <option key={c.id} value={c.id}>
              {c.tradeName ?? c.legalName}
            </option>
          ))}
        </select>
      ) : (
        <span className="text-muted small d-none d-lg-inline">
          {company?.tradeName ?? company?.legalName}
        </span>
      )}
      <div className="d-flex align-items-center gap-1">
        {p.jobsSlot}
        {p.notificationsSlot}
        {p.copilot ? (
          <button
            type="button"
            className={`btn btn-link text-reset${p.copilot.open ? " active" : ""}`}
            onClick={p.copilot.onToggle}
            aria-label="Copiloto"
            title="Copiloto"
            aria-expanded={p.copilot.open}
            aria-controls={p.copilot.panelId}
          >
            <Sparkles size={20} aria-hidden="true" />
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-link text-reset d-none d-md-inline-block"
            disabled
            aria-label="Copiloto indisponível"
            title="Copiloto indisponível para o seu perfil"
          >
            <Sparkles size={20} aria-hidden="true" />
          </button>
        )}
        {/* Nome, tema e sair num menu só: em 390 px o header inteiro cabe na tela. */}
        <HeaderDropdown
          label={`Menu do usuário ${p.userName}`}
          icon={<CircleUserRound size={20} aria-hidden="true" />}
          text={p.userName}
        >
          <div className="px-3 py-2 fw-semibold border-bottom text-truncate">{p.userName}</div>
          <button
            type="button"
            className="dropdown-item d-flex align-items-center gap-2 py-2"
            onClick={() => p.onThemeChange(p.theme === "dark" ? "light" : "dark")}
          >
            {p.theme === "dark" ? (
              <Sun size={16} aria-hidden="true" />
            ) : (
              <Moon size={16} aria-hidden="true" />
            )}
            {p.theme === "dark" ? "Usar tema claro" : "Usar tema escuro"}
          </button>
          <button
            type="button"
            className="dropdown-item d-flex align-items-center gap-2 py-2"
            onClick={p.onLogout}
          >
            <LogOut size={16} aria-hidden="true" />
            Sair
          </button>
        </HeaderDropdown>
      </div>
    </header>
  );
}
